import React, { useEffect, useState } from "react";

interface GuideImageMediaProps {
  src: string;
  alt: string;
  containerClassName?: string;
  imageClassName?: string;
  showMaximizeButton?: boolean;
  loading?: "lazy" | "eager";
}

function GuideImageMedia({
  src,
  alt,
  containerClassName = "",
  imageClassName = "",
  showMaximizeButton = true,
  loading = "lazy",
}: GuideImageMediaProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!isExpanded) {
      setScale(1);
    }
  }, [isExpanded]);

  useEffect(() => {
    setIsLoading(true);
  }, [src]);

  const adjustScale = (delta: number) => {
    setScale((current) =>
      Math.min(2.5, Math.max(1, Number((current + delta).toFixed(2)))),
    );
  };

  return (
    <>
      <div
        className={`relative w-full h-full overflow-hidden bg-secondary-dark/10 ${containerClassName}`}
      >
        {isLoading && (
          <div className="image-skeleton absolute inset-0" aria-hidden="true" />
        )}

        <img
          src={src}
          alt={alt}
          loading={loading}
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
          className={`w-full h-full object-contain object-center transition-opacity duration-300 ${
            isLoading ? "opacity-0" : "opacity-100"
          } ${imageClassName}`}
        />

        {showMaximizeButton && !isLoading && (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="absolute top-3 right-3 rounded-full border border-white/30 bg-black/40 p-2 text-brand-light backdrop-blur-sm transition hover:bg-black/60"
            aria-label={`Open ${alt} in full screen`}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 4H4v5" />
              <path d="M15 4h5v5" />
              <path d="M9 20H4v-5" />
              <path d="M15 20h5v-5" />
            </svg>
          </button>
        )}
      </div>

      {isExpanded && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 px-3 py-4 backdrop-blur-sm sm:px-6"
          role="dialog"
          aria-modal="true"
          aria-label={`Viewing ${alt}`}
        >
          <div className="relative w-full max-w-5xl">
            <div className="absolute right-2 top-2 z-10 flex items-center gap-2">
              <button
                type="button"
                onClick={() => adjustScale(-0.25)}
                className="rounded-full border border-white/20 bg-black/50 p-2 text-brand-light transition hover:bg-black/70"
                aria-label="Zoom out"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => adjustScale(0.25)}
                className="rounded-full border border-white/20 bg-black/50 p-2 text-brand-light transition hover:bg-black/70"
                aria-label="Zoom in"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="rounded-full border border-white/20 bg-black/50 p-2 text-brand-light transition hover:bg-black/70"
                aria-label="Close full screen view"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 6l12 12" />
                  <path d="M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-white/15 bg-black/40 p-2 shadow-2xl sm:p-3">
              <div className="flex min-h-[70vh] items-center justify-center overflow-auto rounded-xl bg-black/20 p-2 sm:p-3">
                <img
                  src={src}
                  alt={alt}
                  style={{ transform: `scale(${scale})` }}
                  className="max-h-[78vh] w-full rounded-xl object-contain transition-transform duration-200"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GuideImageMedia;
